import React, { useState } from 'react';
import './IsometricWhyChooseUs.css';
import {
  Sparkles,
  Heart,
  CircleDollarSign,
  TrendingUp,
  Radio,
  Sliders,
  Star
} from 'lucide-react';

const STAGES = [
  {
    id: 'guest-experience',
    index: '01',
    titleLine1: 'GUEST',
    titleLine2: 'EXPERIENCE',
    position: 'top',
    tagline: 'Seamless Stays & Contactless Digital Concierge',
    description:
      'Delight guests with mobile keys, instant digital concierge requests, personalized amenities, and friction-free contactless check-in.',
    metric: '+98% Guest Delight',
    benefits: [
      'Contactless mobile key & rapid check-in',
      'AI-powered 24/7 digital concierge',
      'Tailored guest stay preferences'
    ],
    color: '#10D864',
    accentColor: '#2EE573',
    badge: '5-Star Hospitality'
  },
  {
    id: 'cost-savings',
    index: '02',
    titleLine1: 'COST',
    titleLine2: 'SAVINGS',
    position: 'bottom',
    tagline: 'Optimized OPEX, Smart Energy & Zero Wastage',
    description:
      'Drastically lower hotel overhead with automated room climate controls, predictive linen inventory, and optimized staff scheduling.',
    metric: '-32% Hotel OPEX',
    benefits: [
      'Smart room energy & utility optimization',
      'Optimized housekeeping shifts & labor',
      'Reduced F&B inventory spoilage'
    ],
    color: '#F59E0B',
    accentColor: '#FACC15',
    badge: 'Financial ROI'
  },
  {
    id: 'revenue-growth',
    index: '03',
    titleLine1: 'REVENUE',
    titleLine2: 'GROWTH',
    position: 'top',
    tagline: 'Dynamic Pricing, Direct Bookings & Upselling',
    description:
      'Maximize room earnings with automated rate optimization, direct booking commissions savings, and high-margin amenity sales.',
    metric: '+28% Revenue Growth',
    benefits: [
      'Automated dynamic rate optimization',
      'Direct booking engine commission savings',
      'Automated room & amenity upselling'
    ],
    color: '#0284C7',
    accentColor: '#38BDF8',
    badge: 'Revenue Uplift'
  },
  {
    id: 'realtime-visibility',
    index: '04',
    titleLine1: 'REAL-TIME',
    titleLine2: 'VISIBILITY',
    position: 'bottom',
    tagline: 'Live Room Status, Housekeeping & Occupancy',
    description:
      'Track room readiness, housekeeping turnover, and guest requests across every floor with instant live telemetry.',
    metric: 'Live 24/7 Floor Grid',
    benefits: [
      'Instant room clean / inspected / occupied status',
      'Real-time guest ticket dispatching',
      'Multi-property occupancy dashboards'
    ],
    color: '#00B4D8',
    accentColor: '#00F0FF',
    badge: 'Live Floor Grid'
  },
  {
    id: 'operational-efficiency',
    index: '05',
    titleLine1: 'OPERATIONAL',
    titleLine2: 'EFFICIENCY',
    position: 'top',
    tagline: 'Automated Workflows & Zero Front-Desk Bottlenecks',
    description:
      'Eliminate check-in queues, automate housekeeping routing, and synchronize front-desk, valet, and F&B operations effortlessly.',
    metric: '10x Faster Turnover',
    benefits: [
      'Automated room turnover routing',
      'Cross-department task synchronization',
      'Zero manual handoff friction'
    ],
    color: '#8B5CF6',
    accentColor: '#C084FC',
    badge: 'Workflow Automation'
  },
  {
    id: 'guest-feedback',
    index: '06',
    titleLine1: 'GUEST',
    titleLine2: 'FEEDBACK',
    position: 'bottom',
    tagline: '5-Star Reviews & Automated Reputation Management',
    description:
      'Capture feedback during the stay to resolve complaints before checkout and drive stellar TripAdvisor and OTA ratings.',
    metric: '4.9 ★ Average NPS',
    benefits: [
      'Real-time in-stay sentiment resolution',
      'Automated post-checkout review capture',
      'Elevated TripAdvisor & Google rank'
    ],
    color: '#EC4899',
    accentColor: '#FF64A5',
    badge: '5-Star Reputation'
  }
];

// =========================================================================
// SCENE 1: GUEST EXPERIENCE (Luxury Bellhop Luggage Trolley, Smart Check-in Kiosk & VIP Guest)
// =========================================================================
const GuestExperienceScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Guest Experience Platform">
    <defs>
      <linearGradient id="topFaceGlow1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#F0FDF4" />
        <stop offset="100%" stopColor="#DCFCE7" />
      </linearGradient>
      <linearGradient id="leftFace1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="goldBrassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="35%" stopColor="#F59E0B" />
        <stop offset="70%" stopColor="#D97706" />
        <stop offset="100%" stopColor="#92400E" />
      </linearGradient>
      <linearGradient id="leatherTan" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FBBF24" />
        <stop offset="50%" stopColor="#D97706" />
        <stop offset="100%" stopColor="#B45309" />
      </linearGradient>
      <linearGradient id="leatherBrown" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#92400E" />
        <stop offset="100%" stopColor="#451A03" />
      </linearGradient>
      <radialGradient id="auraGlow1" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#2EE573" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#10D864" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#10D864" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow1" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
      <filter id="platformDropShadow1" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="6" />
        <feOffset dx="0" dy="10" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="0.25" />
        </feComponentTransfer>
        <feMerge>
          <feMergeNode />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" filter="url(#platformDropShadow1)" />

    {/* Isometric Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace1)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace1)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow1)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Luxury Emerald Welcome Carpet Runner */}
    <polygon points="85,185 160,155 235,185 160,215" fill="#10D864" opacity="0.12" />
    <polygon points="98,185 160,160 222,185 160,210" fill="#10D864" opacity="0.18" />

    {/* Center Radiance Aura */}
    <circle cx="160" cy="125" r="50" fill="url(#auraGlow1)" />

    {/* 3D LUXURY BRASS BELLHOP LUGGAGE TROLLEY (BIRDCAGE CART) */}
    <g transform="translate(130, 160)">
      {/* Cart Ground Shadow */}
      <ellipse cx="0" cy="28" rx="38" ry="12" fill="#000000" opacity="0.2" />

      {/* Cart Base Platform with Emerald Carpet Liner */}
      <polygon points="-36,12 0,26 36,12 0,-2" fill="url(#goldBrassGrad)" stroke="#78350F" strokeWidth="1" />
      <polygon points="-32,12 0,24 32,12 0,0" fill="#064E3B" stroke="#10D864" strokeWidth="1" />
      <polygon points="-36,12 0,26 0,31 -36,17" fill="#78350F" />
      <polygon points="0,26 36,12 36,17 0,31" fill="#92400E" />

      {/* 4 Rubber Caster Wheels */}
      <circle cx="-30" cy="20" r="3.5" fill="#1E293B" stroke="#94A3B8" strokeWidth="1" />
      <circle cx="-2" cy="30" r="3.5" fill="#1E293B" stroke="#94A3B8" strokeWidth="1" />
      <circle cx="30" cy="20" r="3.5" fill="#1E293B" stroke="#94A3B8" strokeWidth="1" />
      <circle cx="0" cy="7" r="3" fill="#1E293B" stroke="#94A3B8" strokeWidth="1" />

      {/* Stacked Luxury Suitcases inside Cart */}
      {/* Large Bottom Suitcase (Tan Leather) */}
      <g transform="translate(0, 10)">
        <polygon points="-20,0 4,-9 24,0 0,9" fill="url(#leatherTan)" stroke="#78350F" strokeWidth="0.8" />
        <polygon points="-20,0 0,9 0,20 -20,11" fill="#B45309" />
        <polygon points="0,9 24,0 24,11 0,20" fill="#92400E" />
        {/* Leather Straps & Brass Buckles */}
        <line x1="-10" y1="5" x2="-10" y2="15" stroke="#78350F" strokeWidth="1.8" />
        <line x1="12" y1="-4" x2="12" y2="6" stroke="#78350F" strokeWidth="1.8" />
        <circle cx="-10" cy="10" r="1.5" fill="#FEF08A" />
        <circle cx="12" cy="1" r="1.5" fill="#FEF08A" />
      </g>

      {/* Medium Top Suitcase (Deep Brown) */}
      <g transform="translate(2, 0)">
        <polygon points="-16,-2 4,-10 20,-2 0,6" fill="url(#leatherBrown)" stroke="#451A03" strokeWidth="0.8" />
        <polygon points="-16,-2 0,6 0,14 -16,6" fill="#78350F" />
        <polygon points="0,6 20,-2 20,6 0,14" fill="#5C2508" />
        {/* Handle */}
        <path d="M -4 2 Q 0 -3 4 2" fill="none" stroke="#FDE047" strokeWidth="1.5" />
      </g>

      {/* Hanging Garment Bag */}
      <polygon points="-22,-2 -14,-8 -12,12 -20,18" fill="#1E293B" stroke="#475569" strokeWidth="0.8" />
      <line x1="-18" y1="-5" x2="-16" y2="15" stroke="#94A3B8" strokeWidth="1" />

      {/* Polished Brass Birdcage Tubular Frame */}
      {/* 4 Vertical Brass Corner Posts */}
      <line x1="-34" y1="12" x2="-34" y2="-45" stroke="url(#goldBrassGrad)" strokeWidth="3" strokeLinecap="round" />
      <line x1="34" y1="12" x2="34" y2="-45" stroke="url(#goldBrassGrad)" strokeWidth="3" strokeLinecap="round" />
      <line x1="0" y1="26" x2="0" y2="-35" stroke="url(#goldBrassGrad)" strokeWidth="3" strokeLinecap="round" />
      <line x1="0" y1="-2" x2="0" y2="-55" stroke="url(#goldBrassGrad)" strokeWidth="2.5" strokeLinecap="round" />

      {/* Top Arched Birdcage Canopy */}
      <path d="M -34 -45 C -34 -72, 34 -72, 34 -45" fill="none" stroke="url(#goldBrassGrad)" strokeWidth="3" strokeLinecap="round" filter="url(#softGlow1)" />
      <path d="M 0 -35 C 0 -62, 0 -62, 0 -55" fill="none" stroke="url(#goldBrassGrad)" strokeWidth="3" />
      {/* Top Finial Ring */}
      <circle cx="0" cy="-68" r="5" fill="none" stroke="url(#goldBrassGrad)" strokeWidth="2.5" />
      <circle cx="0" cy="-63" r="3" fill="#FDE047" />
    </g>

    {/* SMART CONTACTLESS CHECK-IN KIOSK TERMINAL */}
    <g transform="translate(210, 145)">
      {/* Kiosk Pedestal Base */}
      <polygon points="-18,0 0,8 18,0 0,-8" fill="#0F172A" />
      <polygon points="-18,0 0,8 0,38 -18,30" fill="#1E293B" stroke="#334155" strokeWidth="1" />
      <polygon points="0,8 18,0 18,30 0,38" fill="#334155" />

      {/* Sleek Upright Glass Touchscreen */}
      <polygon points="-16,-4 0,3 16,-4 0,-11" fill="#022C22" stroke="#10D864" strokeWidth="1.2" />
      <polygon points="-16,-4 0,3 0,-40 -16,-47" fill="#064E3B" stroke="#10D864" strokeWidth="1.2" filter="url(#softGlow1)" />
      <polygon points="0,3 16,-4 16,-47 0,-40" fill="#022C22" stroke="#10D864" strokeWidth="1.2" />

      {/* Screen Interface: Mobile Key & NFC Wave */}
      <g transform="translate(-8, -25)">
        <rect x="0" y="0" width="16" height="12" rx="2" fill="#10D864" opacity="0.25" />
        <circle cx="5" cy="6" r="2.5" fill="#FEF08A" />
        <path d="M 9 3 A 4 4 0 0 1 9 9" fill="none" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
        <path d="M 12 1 A 7 7 0 0 1 12 11" fill="none" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" />
      </g>

      {/* Dispensed VIP Key Card */}
      <polygon points="-12,-3 6,-10 14,-6 -4,1" fill="#FEF08A" stroke="#B45309" strokeWidth="0.8" />
      <circle cx="1" cy="-4" r="1.5" fill="#10D864" />
    </g>

    {/* 5 FLOATING GOLDEN STARS ARC */}
    <g transform="translate(160, 52)">
      {[-32, -16, 0, 16, 32].map((xOffset, i) => {
        const yOffset = Math.abs(xOffset) * 0.12;
        return (
          <path
            key={i}
            transform={`translate(${xOffset}, ${yOffset}) scale(0.95)`}
            d="M 0 -8 L 2.4 -2.4 L 8.4 -1.6 L 4 -0.2 L 5 5.8 L 0 3 L -5 5.8 L -4 -0.2 L -8.4 -1.6 L -2.4 -2.4 Z"
            fill="#FACC15"
            stroke="#EAB308"
            strokeWidth="0.8"
            filter="url(#softGlow1)"
          />
        );
      })}
    </g>

    {/* Floating 5-Star Stay Metric Badge */}
    <g transform="translate(196, 62)">
      <rect x="0" y="0" width="76" height="22" rx="11" fill="#064E3B" stroke="#2EE573" strokeWidth="1.4" />
      <circle cx="11" cy="11" r="3.5" fill="#2EE573" />
      <text x="43" y="15" fill="#FFFFFF" fontSize="8.5" fontWeight="800" textAnchor="middle">
        ★ 5-STAR STAY
      </text>
    </g>

    {/* Figures: Professional Hotel Bellhop & Welcomed Guest */}
    {/* Bellhop Figure */}
    <g transform="translate(68, 142)">
      <ellipse cx="0" cy="40" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="6" r="5" fill="#FCD34D" />
      <path d="M -4 4 C -4 0, 4 0, 4 4 Z" fill="#1E293B" />
      {/* Bellhop Pillbox Cap with Gold Trim */}
      <rect x="-4" y="0" width="8" height="3" rx="1" fill="#B91C1C" />
      <line x1="-4" y1="3" x2="4" y2="3" stroke="#FDE047" strokeWidth="0.8" />
      {/* Red & Gold Vest */}
      <polygon points="-7,12 7,12 5,30 -5,30" fill="#B91C1C" />
      <polygon points="-2,12 2,12 2,30 -2,30" fill="#FDE047" />
      {/* Trousers */}
      <line x1="-3" y1="30" x2="-3" y2="40" stroke="#1E293B" strokeWidth="3.2" strokeLinecap="round" />
      <line x1="3" y1="30" x2="3" y2="40" stroke="#1E293B" strokeWidth="3.2" strokeLinecap="round" />
      {/* Hand Welcoming to Luggage Cart */}
      <line x1="5" y1="16" x2="16" y2="24" stroke="#B91C1C" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="17" cy="25" r="1.5" fill="#FCD34D" />
    </g>

    {/* Delighted Guest Figure */}
    <g transform="translate(245, 148)">
      <ellipse cx="0" cy="38" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="4" r="5" fill="#FDE68A" />
      <path d="M -4 2 C -4 -2, 4 -2, 4 2 Z" fill="#78350F" />
      <rect x="-6" y="10" width="12" height="18" rx="3" fill="#0284C7" />
      <line x1="-3" y1="28" x2="-3" y2="38" stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="28" x2="3" y2="38" stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      <line x1="-5" y1="14" x2="-12" y2="22" stroke="#0284C7" strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="-13" cy="23" r="1.5" fill="#FDE68A" />
    </g>
  </svg>
);

// =========================================================================
// SCENE 2: COST SAVINGS (Reinforced Hotel OPEX Vault, Gold Coins, Smart Eco Energy)
// =========================================================================
const CostSavingsScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Cost Savings Platform">
    <defs>
      <linearGradient id="topFaceGlow2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#FFFBEB" />
        <stop offset="100%" stopColor="#FEF3C7" />
      </linearGradient>
      <linearGradient id="leftFace2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="goldCoin2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FDE047" />
        <stop offset="50%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>
      <linearGradient id="goldBarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="50%" stopColor="#EAB308" />
        <stop offset="100%" stopColor="#B45309" />
      </linearGradient>
      <linearGradient id="vaultSteel" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#64748B" />
        <stop offset="50%" stopColor="#334155" />
        <stop offset="100%" stopColor="#1E293B" />
      </linearGradient>
      <linearGradient id="vaultInner2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0F172A" />
        <stop offset="100%" stopColor="#020617" />
      </linearGradient>
      <radialGradient id="goldGlow2" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.8" />
        <stop offset="60%" stopColor="#FBBF24" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow2" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="3" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" />

    {/* Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace2)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace2)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow2)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Golden Radiance Aura */}
    <circle cx="160" cy="135" r="55" fill="url(#goldGlow2)" />

    {/* 3D HOTEL OPEX STEEL VAULT */}
    <g transform="translate(175, 125)">
      {/* Outer Vault Frame */}
      <polygon points="0,0 48,-24 48,50 0,74" fill="#1E293B" stroke="#0F172A" strokeWidth="1.5" />
      <polygon points="-48,-24 0,0 0,74 -48,50" fill="url(#vaultSteel)" stroke="#0F172A" strokeWidth="1.5" />
      <polygon points="0,-48 48,-24 0,0 -48,-24" fill="#475569" stroke="#334155" strokeWidth="1.5" />

      {/* Interior Chamber */}
      <polygon points="-42,-18 -4,1 -4,65 -42,46" fill="url(#vaultInner2)" />
      <polygon points="-4,1 42,-18 42,46 -4,65" fill="#0B132B" />

      {/* Gold Bullion Bars Inside Vault */}
      <g transform="translate(-24, 25)">
        <polygon points="0,0 16,-8 32,0 16,8" fill="url(#goldBarGrad)" />
        <polygon points="0,0 16,8 16,14 0,6" fill="#B45309" />
        <polygon points="16,8 32,0 32,6 16,14" fill="#78350F" />
      </g>
      <g transform="translate(-20, 10)">
        <polygon points="0,0 14,-7 28,0 14,7" fill="url(#goldBarGrad)" />
        <polygon points="0,0 14,7 14,12 0,5" fill="#B45309" />
        <polygon points="14,7 28,0 28,5 14,12" fill="#78350F" />
      </g>

      {/* Open Vault Door Swung Open to Right */}
      <polygon points="48,-24 78,-9 78,65 48,50" fill="#334155" stroke="#1E293B" strokeWidth="1.5" />
      <polygon points="48,-24 78,-9 58,1 28,-14" fill="#64748B" />
      {/* Heavy Combination Wheel */}
      <circle cx="63" cy="20" r="10" fill="#94A3B8" stroke="#0F172A" strokeWidth="2" />
      <circle cx="63" cy="20" r="5" fill="#F59E0B" />
      {/* Heavy Steel Locking Bolts */}
      <rect x="75" y="0" width="6" height="4" fill="#CBD5E1" rx="1" />
      <rect x="75" y="24" width="6" height="4" fill="#CBD5E1" rx="1" />
      <rect x="75" y="48" width="6" height="4" fill="#CBD5E1" rx="1" />
      {/* Eco Leaf Badge on Vault (Energy & Utility Savings) */}
      <circle cx="63" cy="-2" r="5" fill="#10D864" />
      <path d="M 61 -3 C 61 -6, 65 -6, 65 -3 C 65 0, 61 0, 61 -3 Z" fill="#FFFFFF" />
    </g>

    {/* FOREGROUND GLEAMING GOLD COIN STACKS */}
    {/* Stack 1 (Tall) */}
    <g transform="translate(100, 192)">
      {[0, 4, 8, 12, 16, 20].map((dy) => (
        <g key={dy} transform={`translate(0, ${-dy})`}>
          <ellipse cx="0" cy="0" rx="15" ry="6" fill="#B45309" />
          <ellipse cx="0" cy="-1.5" rx="15" ry="6" fill="url(#goldCoin2)" />
        </g>
      ))}
      <text x="0" y="-20" fill="#78350F" fontSize="7" fontWeight="900" textAnchor="middle">
        $
      </text>
    </g>

    {/* Stack 2 (Medium) */}
    <g transform="translate(125, 202)">
      {[0, 4, 8, 12].map((dy) => (
        <g key={dy} transform={`translate(0, ${-dy})`}>
          <ellipse cx="0" cy="0" rx="13" ry="5.5" fill="#B45309" />
          <ellipse cx="0" cy="-1.5" rx="13" ry="5.5" fill="url(#goldCoin2)" />
        </g>
      ))}
      <text x="0" y="-12" fill="#78350F" fontSize="6.5" fontWeight="900" textAnchor="middle">
        $
      </text>
    </g>

    {/* Stack 3 (Foreground Small) */}
    <g transform="translate(82, 202)">
      {[0, 4, 8].map((dy) => (
        <g key={dy} transform={`translate(0, ${-dy})`}>
          <ellipse cx="0" cy="0" rx="12" ry="5" fill="#B45309" />
          <ellipse cx="0" cy="-1.5" rx="12" ry="5" fill="url(#goldCoin2)" />
        </g>
      ))}
    </g>

    {/* Floating -32% OPEX Badge */}
    <g transform="translate(142, 60)">
      <rect x="0" y="0" width="70" height="22" rx="11" fill="#78350F" stroke="#F59E0B" strokeWidth="1.5" />
      <polygon points="10,13 14,8 18,13" fill="#10D864" />
      <text x="40" y="15" fill="#FEF08A" fontSize="9" fontWeight="800" textAnchor="middle">
        -32% OPEX
      </text>
    </g>

    {/* Figure: Hotel Financial Controller with Clipboard */}
    <g transform="translate(68, 145)">
      <ellipse cx="0" cy="40" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="6" r="5" fill="#FDE68A" />
      <path d="M -4 4 C -4 0, 4 0, 4 4 Z" fill="#1E293B" />
      <rect x="-6" y="12" width="12" height="18" rx="2" fill="#F59E0B" />
      <line x1="-3" y1="30" x2="-3" y2="40" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="30" x2="3" y2="40" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      {/* Arm holding clipboard */}
      <line x1="5" y1="16" x2="14" y2="22" stroke="#F59E0B" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="12" y="16" width="10" height="14" rx="1" fill="#FFFFFF" stroke="#0F172A" strokeWidth="1" />
      <line x1="14" y1="19" x2="20" y2="19" stroke="#10D864" strokeWidth="1.2" />
      <line x1="14" y1="23" x2="19" y2="23" stroke="#F59E0B" strokeWidth="1.2" />
      <line x1="14" y1="27" x2="18" y2="27" stroke="#334155" strokeWidth="1.2" />
    </g>
  </svg>
);

// =========================================================================
// SCENE 3: REVENUE GROWTH (RevPAR Yield Bars, Dynamic Pricing Surge & Booking Hub)
// =========================================================================
const RevenueGrowthScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Revenue Growth Platform">
    <defs>
      <linearGradient id="topFaceGlow3" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#F0F9FF" />
        <stop offset="100%" stopColor="#E0F2FE" />
      </linearGradient>
      <linearGradient id="leftFace3" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace3" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="barGrad1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="100%" stopColor="#0284C7" />
      </linearGradient>
      <linearGradient id="barGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#2563EB" />
      </linearGradient>
      <linearGradient id="barGrad3" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#818CF8" />
        <stop offset="100%" stopColor="#4F46E5" />
      </linearGradient>
      <radialGradient id="revGlow3" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#38BDF8" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#0284C7" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#0284C7" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow3" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" />

    {/* Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace3)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace3)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow3)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Cyan Core Radiance */}
    <circle cx="160" cy="120" r="50" fill="url(#revGlow3)" />

    {/* ASCENDING 3D ISOMETRIC REVPAR BAR PILLARS */}
    {/* Bar 1 (Base Tier - ADR) */}
    <g transform="translate(100, 185)">
      <polygon points="0,0 20,-10 40,0 20,10" fill="#38BDF8" />
      <polygon points="0,0 20,10 20,40 0,30" fill="#0284C7" />
      <polygon points="20,10 40,0 40,30 20,40" fill="#0369A1" />
      <text x="20" y="2" fill="#FFFFFF" fontSize="6" fontWeight="800" textAnchor="middle">
        ADR
      </text>
    </g>

    {/* Bar 2 (Mid Tier - Direct Bookings) */}
    <g transform="translate(140, 160)">
      <polygon points="0,0 22,-11 44,0 22,11" fill="#60A5FA" />
      <polygon points="0,0 22,11 22,65 0,54" fill="#2563EB" />
      <polygon points="22,11 44,0 44,54 22,65" fill="#1D4ED8" />
      <text x="22" y="2" fill="#FFFFFF" fontSize="6.5" fontWeight="800" textAnchor="middle">
        DIRECT
      </text>
    </g>

    {/* Bar 3 (Peak Tier - Max RevPAR Yield) */}
    <g transform="translate(182, 132)">
      <polygon points="0,0 24,-12 48,0 24,12" fill="#A5F3FC" stroke="#FFFFFF" strokeWidth="1" filter="url(#softGlow3)" />
      <polygon points="0,0 24,12 24,94 0,82" fill="url(#barGrad1)" />
      <polygon points="24,12 48,0 48,82 24,94" fill="#0369A1" />
      {/* Peak Light Beam */}
      <line x1="24" y1="-12" x2="24" y2="-35" stroke="#38BDF8" strokeWidth="2.5" strokeDasharray="4 2" />
      <circle cx="24" cy="-36" r="3.5" fill="#38BDF8" filter="url(#softGlow3)" />
      <text x="24" y="3" fill="#0F172A" fontSize="7" fontWeight="900" textAnchor="middle">
        GROWTH
      </text>
    </g>

    {/* DYNAMIC RATE ASCENDING SURGE CURVE */}
    <path
      d="M 120 175 Q 162 145, 206 115"
      fill="none"
      stroke="#FDE047"
      strokeWidth="3.5"
      strokeLinecap="round"
      filter="url(#softGlow3)"
    />
    <polygon points="206,110 216,115 208,122" fill="#FDE047" />

    {/* Floating Amenity Upsell Badges */}
    <g transform="translate(90, 100)">
      <rect x="0" y="0" width="46" height="16" rx="8" fill="#1E293B" stroke="#38BDF8" strokeWidth="1" />
      <circle cx="8" cy="8" r="2.5" fill="#38BDF8" />
      <text x="26" y="11" fill="#FFFFFF" fontSize="6" fontWeight="800" textAnchor="middle">
        SUITE UP
      </text>
    </g>
    <g transform="translate(200, 75)">
      <rect x="0" y="0" width="48" height="16" rx="8" fill="#1E293B" stroke="#F59E0B" strokeWidth="1" />
      <circle cx="8" cy="8" r="2.5" fill="#F59E0B" />
      <text x="27" y="11" fill="#FFFFFF" fontSize="6" fontWeight="800" textAnchor="middle">
        AMENITY+
      </text>
    </g>

    {/* Floating Revenue Growth Metric Tag */}
    <g transform="translate(136, 50)">
      <rect x="0" y="0" width="76" height="22" rx="11" fill="#0C4A6E" stroke="#38BDF8" strokeWidth="1.5" />
      <polygon points="12,14 16,8 20,14" fill="#38BDF8" />
      <text x="44" y="15" fill="#FFFFFF" fontSize="8" fontWeight="800" textAnchor="middle">
        +28% REVENUE
      </text>
    </g>

    {/* Figure: Hotel Revenue Strategist with Live Chart Tablet */}
    <g transform="translate(70, 148)">
      <ellipse cx="0" cy="40" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="6" r="5" fill="#FCD34D" />
      <path d="M -4 4 C -4 0, 4 0, 4 4 Z" fill="#1E293B" />
      <rect x="-6" y="12" width="12" height="18" rx="2" fill="#0284C7" />
      <line x1="-3" y1="30" x2="-3" y2="40" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="30" x2="3" y2="40" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      {/* Arm pointing up to peak bar */}
      <line x1="5" y1="16" x2="18" y2="10" stroke="#0284C7" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="19" cy="9" r="1.5" fill="#FCD34D" />
    </g>
  </svg>
);

// =========================================================================
// SCENE 4: REAL-TIME VISIBILITY (3D Multi-Floor Hotel Wing, Live Room Status Grid & Dispatch)
// =========================================================================
const RealtimeVisibilityScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Real-Time Visibility Platform">
    <defs>
      <linearGradient id="topFaceGlow4" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#F0FDFA" />
        <stop offset="100%" stopColor="#CCFBF1" />
      </linearGradient>
      <linearGradient id="leftFace4" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace4" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="hotelGlass" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#E0F2FE" />
        <stop offset="50%" stopColor="#38BDF8" />
        <stop offset="100%" stopColor="#0284C7" />
      </linearGradient>
      <linearGradient id="hotelWallSlate" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#334155" />
        <stop offset="100%" stopColor="#0F172A" />
      </linearGradient>
      <radialGradient id="cyanAura4" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#06B6D4" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow4" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" />

    {/* Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace4)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace4)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow4)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Cyan Radiance Halo */}
    <circle cx="160" cy="130" r="50" fill="url(#cyanAura4)" />

    {/* 3D ISOMETRIC MULTI-FLOOR HOTEL TOWER (WING CUTAWAY) */}
    <g transform="translate(195, 140)">
      {/* Ground Shadow */}
      <ellipse cx="0" cy="40" rx="42" ry="15" fill="#000000" opacity="0.25" />

      {/* Hotel Building Base & Main Structure */}
      {/* Roof */}
      <polygon points="0,-72 38,-53 0,-34 -38,-53" fill="#1E293B" stroke="#00F0FF" strokeWidth="1.5" />
      {/* Roof Skylight / Penthouse Feature */}
      <polygon points="0,-68 18,-59 0,-50 -18,-59" fill="#0284C7" opacity="0.8" />
      {/* Left Wall (Glass Window Bays) */}
      <polygon points="-38,-53 0,-34 0,42 -38,23" fill="url(#hotelWallSlate)" stroke="#0F172A" strokeWidth="1.2" />
      {/* Right Wall */}
      <polygon points="0,-34 38,-53 38,23 0,42" fill="#1E293B" stroke="#0F172A" strokeWidth="1.2" />

      {/* FLOOR 3: PENTHOUSE SUITES */}
      {/* Left Windows Floor 3 */}
      <polygon points="-32,-45 -6,-32 -6,-20 -32,-33" fill="#0369A1" stroke="#00F0FF" strokeWidth="1" />
      {/* Room 301 Status: Green (Clean / Ready) */}
      <circle cx="-20" cy="-35" r="2.5" fill="#10D864" filter="url(#softGlow4)" />
      {/* Room 302 Status: Blue (Occupied) */}
      <circle cx="-10" cy="-29" r="2.5" fill="#00F0FF" filter="url(#softGlow4)" />
      {/* Right Windows Floor 3 */}
      <polygon points="6,-32 32,-45 32,-33 6,-20" fill="#0F172A" stroke="#0284C7" strokeWidth="0.8" />
      <circle cx="18" cy="-35" r="2.5" fill="#10D864" />

      {/* FLOOR 2: DELUXE ROOMS */}
      {/* Left Windows Floor 2 */}
      <polygon points="-32,-16 -6,-3 -6,9 -32,-4" fill="#0369A1" stroke="#00F0FF" strokeWidth="1" />
      {/* Room 201 Status: Amber (Housekeeping In Progress) */}
      <circle cx="-20" cy="-6" r="2.5" fill="#F59E0B" filter="url(#softGlow4)" />
      {/* Room 202 Status: Green (Clean / Ready) */}
      <circle cx="-10" cy="0" r="2.5" fill="#10D864" filter="url(#softGlow4)" />
      {/* Right Windows Floor 2 */}
      <polygon points="6,-3 32,-16 32,-4 6,9" fill="#0F172A" stroke="#0284C7" strokeWidth="0.8" />
      <circle cx="18" cy="-6" r="2.5" fill="#00F0FF" />

      {/* FLOOR 1: EXECUTIVE & LOBBY */}
      {/* Left Windows Floor 1 */}
      <polygon points="-32,13 -6,26 -6,38 -32,25" fill="#0369A1" stroke="#00F0FF" strokeWidth="1" />
      {/* Room 101 Status: Blue (Occupied) */}
      <circle cx="-20" cy="23" r="2.5" fill="#00F0FF" filter="url(#softGlow4)" />
      {/* Room 102 Status: Green (Clean / Ready) */}
      <circle cx="-10" cy="29" r="2.5" fill="#10D864" filter="url(#softGlow4)" />
      {/* Right Windows Floor 1 */}
      <polygon points="6,26 32,13 32,25 6,38" fill="#0F172A" stroke="#0284C7" strokeWidth="0.8" />
      <circle cx="18" cy="23" r="2.5" fill="#10D864" />

      {/* Modern Hotel Entrance Canopy */}
      <polygon points="-14,35 14,21 14,24 -14,38" fill="#00F0FF" />
    </g>

    {/* STANDING DIGITAL LIVE ROOM STATUS BOARD */}
    <g transform="translate(95, 145)">
      {/* Base Stand */}
      <polygon points="-5,35 5,35 7,45 -7,45" fill="#1E293B" />
      <line x1="0" y1="20" x2="0" y2="40" stroke="#0F172A" strokeWidth="3" />

      {/* High-Tech Glass Display Screen */}
      <rect x="-38" y="-36" width="76" height="58" rx="6" fill="#082F49" stroke="#00F0FF" strokeWidth="1.5" filter="url(#softGlow4)" />
      {/* Screen Header */}
      <rect x="-34" y="-32" width="68" height="12" rx="3" fill="#0C4A6E" />
      <text x="0" y="-23" fill="#38BDF8" fontSize="6.5" fontWeight="800" textAnchor="middle" letterSpacing="0.05em">
        LIVE HOTEL GRID
      </text>

      {/* Live Room Status Indicator Matrix */}
      {/* Room 101-104 */}
      <g transform="translate(-28, -13)">
        <circle cx="4" cy="4" r="3" fill="#10D864" />
        <circle cx="16" cy="4" r="3" fill="#10D864" />
        <circle cx="28" cy="4" r="3" fill="#F59E0B" />
        <circle cx="40" cy="4" r="3" fill="#00F0FF" />
        <circle cx="52" cy="4" r="3" fill="#10D864" />
      </g>
      {/* Room 201-204 */}
      <g transform="translate(-28, -2)">
        <circle cx="4" cy="4" r="3" fill="#00F0FF" />
        <circle cx="16" cy="4" r="3" fill="#10D864" />
        <circle cx="28" cy="4" r="3" fill="#10D864" />
        <circle cx="40" cy="4" r="3" fill="#F59E0B" />
        <circle cx="52" cy="4" r="3" fill="#00F0FF" />
      </g>

      {/* Occupancy Rate Bar & Metric */}
      <rect x="-30" y="10" width="60" height="7" rx="3.5" fill="#0369A1" />
      <rect x="-30" y="10" width="54" height="7" rx="3.5" fill="#00F0FF" />
      <text x="0" y="16" fill="#082F49" fontSize="5.5" fontWeight="900" textAnchor="middle">
        94% OCCUPIED • 48/50 READY
      </text>
    </g>

    {/* Floating LIVE 24/7 Metric Badge */}
    <g transform="translate(192, 55)">
      <rect x="0" y="0" width="68" height="22" rx="11" fill="#083344" stroke="#00F0FF" strokeWidth="1.5" />
      <circle cx="11" cy="11" r="3.5" fill="#10D864" />
      <text x="40" y="15" fill="#FFFFFF" fontSize="8.5" fontWeight="800" textAnchor="middle">
        LIVE 24/7
      </text>
    </g>

    {/* Figure: Floor Supervisor with Real-Time Tablet */}
    <g transform="translate(48, 150)">
      <ellipse cx="0" cy="40" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="6" r="5" fill="#FCD34D" />
      <path d="M -4 4 C -4 0, 4 0, 4 4 Z" fill="#083344" />
      <rect x="-6" y="12" width="12" height="18" rx="2" fill="#0E7490" />
      <line x1="-3" y1="30" x2="-3" y2="40" stroke="#083344" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="30" x2="3" y2="40" stroke="#083344" strokeWidth="3" strokeLinecap="round" />
      {/* Hand holding live tablet */}
      <line x1="5" y1="16" x2="16" y2="20" stroke="#0E7490" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="14" y="14" width="12" height="15" rx="2" fill="#083344" stroke="#22D3EE" strokeWidth="1.2" />
      <circle cx="20" cy="21" r="2.5" fill="#10D864" />
    </g>
  </svg>
);

// =========================================================================
// SCENE 5: OPERATIONAL EFFICIENCY (Hotel Housekeeping Service Cart & Automated Dispatch Board)
// =========================================================================
const OperationalEfficiencyScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Operational Efficiency Platform">
    <defs>
      <linearGradient id="topFaceGlow5" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#FAF5FF" />
        <stop offset="100%" stopColor="#F3E8FF" />
      </linearGradient>
      <linearGradient id="leftFace5" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace5" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="trolleyMetal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#6B21A8" />
        <stop offset="50%" stopColor="#4C1D95" />
        <stop offset="100%" stopColor="#1E1B4B" />
      </linearGradient>
      <linearGradient id="towelWhite" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#F8FAFC" />
        <stop offset="100%" stopColor="#E2E8F0" />
      </linearGradient>
      <radialGradient id="purpleGlow5" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#C084FC" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#8B5CF6" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow5" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" />

    {/* Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace5)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace5)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow5)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Radiant Purple Halo */}
    <circle cx="160" cy="130" r="50" fill="url(#purpleGlow5)" />

    {/* 3D HOTEL HOUSEKEEPING & ROOM SERVICE CART */}
    <g transform="translate(130, 160)">
      {/* Ground Shadow */}
      <ellipse cx="0" cy="30" rx="36" ry="12" fill="#000000" opacity="0.2" />

      {/* Cart Base Frame with Rubber Bumpers */}
      <polygon points="-32,10 0,22 32,10 0,-2" fill="url(#trolleyMetal)" stroke="#581C87" strokeWidth="1" />
      <polygon points="-32,10 0,22 0,28 -32,16" fill="#3B0764" />
      <polygon points="0,22 32,10 32,16 0,28" fill="#581C87" />

      {/* 4 Rubber Wheels */}
      <circle cx="-26" cy="18" r="3.5" fill="#1E1B4B" stroke="#94A3B8" strokeWidth="1" />
      <circle cx="-2" cy="28" r="3.5" fill="#1E1B4B" stroke="#94A3B8" strokeWidth="1" />
      <circle cx="26" cy="18" r="3.5" fill="#1E1B4B" stroke="#94A3B8" strokeWidth="1" />

      {/* Cart Middle Shelf */}
      <polygon points="-28,-2 0,8 28,-2 0,-12" fill="#6B21A8" stroke="#A855F7" strokeWidth="0.8" />
      <polygon points="-28,-2 0,8 0,12 -28,2" fill="#3B0764" />
      <polygon points="0,8 28,-2 28,2 0,12" fill="#581C87" />

      {/* Stacked Fresh White Bed Linens on Middle Shelf */}
      <polygon points="-18,-1 0,6 18,-1 0,-8" fill="url(#towelWhite)" stroke="#CBD5E1" strokeWidth="0.8" />
      <polygon points="-18,2 0,9 18,2 0,-5" fill="url(#towelWhite)" stroke="#CBD5E1" strokeWidth="0.8" />

      {/* Cart Top Shelf */}
      <polygon points="-28,-18 0,-8 28,-18 0,-28" fill="#7E22CE" stroke="#C084FC" strokeWidth="0.8" />

      {/* Stack of Clean Folded Towels on Top Shelf */}
      <g transform="translate(-10, -22)">
        <polygon points="-12,0 0,5 12,0 0,-5" fill="url(#towelWhite)" stroke="#CBD5E1" strokeWidth="0.8" />
        <polygon points="-12,3 0,8 12,3 0,-2" fill="url(#towelWhite)" stroke="#CBD5E1" strokeWidth="0.8" />
        <polygon points="-12,6 0,11 12,6 0,1" fill="url(#towelWhite)" stroke="#CBD5E1" strokeWidth="0.8" />
      </g>

      {/* Amenity Bottles & Toiletries on Top Shelf */}
      <g transform="translate(10, -20)">
        <rect x="-4" y="-8" width="4" height="8" rx="1" fill="#C084FC" />
        <rect x="2" y="-10" width="4.5" height="10" rx="1" fill="#22D3EE" />
        <circle cx="-2" cy="-9" r="1" fill="#FFFFFF" />
        <circle cx="4.25" cy="-11" r="1.2" fill="#FFFFFF" />
      </g>

      {/* 4 Vertical Tubular Chrome Corner Handles */}
      <line x1="-28" y1="10" x2="-28" y2="-25" stroke="#E2E8F0" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="28" y1="10" x2="28" y2="-25" stroke="#E2E8F0" strokeWidth="2.5" strokeLinecap="round" />
      {/* Push Handle Bar */}
      <path d="M 28 -25 Q 35 -30 28 -35" fill="none" stroke="#E2E8F0" strokeWidth="2.5" strokeLinecap="round" />

      {/* Side Laundry Utility Bag */}
      <polygon points="-30,6 -20,10 -22,22 -32,18" fill="#475569" stroke="#334155" strokeWidth="1" />
    </g>

    {/* STANDING AUTOMATED DISPATCH KANBAN SCREEN */}
    <g transform="translate(205, 142)">
      {/* Stand Pedestal */}
      <polygon points="-14,0 0,6 14,0 0,-6" fill="#0F172A" />
      <polygon points="-14,0 0,6 0,34 -14,28" fill="#1E1B4B" stroke="#334155" strokeWidth="1" />
      <polygon points="0,6 14,0 14,28 0,34" fill="#334155" />

      {/* Upright Glass Operations Screen */}
      <rect x="-34" y="-48" width="68" height="52" rx="5" fill="#1E1B4B" stroke="#A855F7" strokeWidth="1.5" filter="url(#softGlow5)" />
      {/* Screen Title */}
      <rect x="-30" y="-44" width="60" height="10" rx="2" fill="#3B0764" />
      <text x="0" y="-37" fill="#E9D5FF" fontSize="6" fontWeight="800" textAnchor="middle" letterSpacing="0.05em">
        TASK DISPATCH
      </text>

      {/* 3 Kanban Columns: QUEUE, ACTIVE, DONE */}
      {/* Column 1: QUEUE */}
      <g transform="translate(-24, -28)">
        <rect x="0" y="0" width="14" height="26" rx="2" fill="#0F172A" opacity="0.6" />
        <rect x="2" y="3" width="10" height="6" rx="1" fill="#64748B" />
        <rect x="2" y="11" width="10" height="6" rx="1" fill="#64748B" />
        <circle cx="7" cy="21" r="1.5" fill="#F59E0B" />
      </g>
      {/* Column 2: ACTIVE */}
      <g transform="translate(-7, -28)">
        <rect x="0" y="0" width="14" height="26" rx="2" fill="#3B0764" stroke="#C084FC" strokeWidth="0.8" />
        <rect x="2" y="3" width="10" height="8" rx="1" fill="#8B5CF6" />
        <circle cx="7" cy="7" r="1.5" fill="#FFFFFF" />
        <rect x="2" y="13" width="10" height="8" rx="1" fill="#A855F7" />
      </g>
      {/* Column 3: DONE */}
      <g transform="translate(10, -28)">
        <rect x="0" y="0" width="14" height="26" rx="2" fill="#064E3B" stroke="#10D864" strokeWidth="0.8" />
        <rect x="2" y="3" width="10" height="6" rx="1" fill="#10D864" />
        <polyline points="4,6 6,8 10,4" fill="none" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" />
        <rect x="2" y="11" width="10" height="6" rx="1" fill="#10D864" />
        <polyline points="4,14 6,16 10,12" fill="none" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" />
      </g>
    </g>

    {/* Floating 10x Faster Metric Badge */}
    <g transform="translate(186, 52)">
      <rect x="0" y="0" width="72" height="22" rx="11" fill="#3B0764" stroke="#C084FC" strokeWidth="1.5" />
      <circle cx="11" cy="11" r="3.5" fill="#C084FC" />
      <text x="42" y="15" fill="#FFFFFF" fontSize="8.5" fontWeight="800" textAnchor="middle">
        10x FASTER
      </text>
    </g>

    {/* Figure: Operations Supervisor with Checklist Tablet */}
    <g transform="translate(62, 145)">
      <ellipse cx="0" cy="40" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="6" r="5" fill="#FCD34D" />
      <path d="M -4 4 C -4 0, 4 0, 4 4 Z" fill="#1E1B4B" />
      <rect x="-6" y="12" width="12" height="18" rx="2" fill="#7C3AED" />
      <line x1="-3" y1="30" x2="-3" y2="40" stroke="#1E1B4B" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="30" x2="3" y2="40" stroke="#1E1B4B" strokeWidth="3" strokeLinecap="round" />
      {/* Arm holding checklist tablet */}
      <line x1="5" y1="16" x2="15" y2="22" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" />
      <rect x="13" y="16" width="11" height="13" rx="1.5" fill="#1E1B4B" stroke="#C084FC" strokeWidth="1" />
      <polyline points="16,22 18,24 21,20" fill="none" stroke="#10D864" strokeWidth="1.2" strokeLinecap="round" />
    </g>
  </svg>
);

// =========================================================================
// SCENE 6: GUEST FEEDBACK (Grand 5-Star Golden Trophy, Verified Review Plaque & Delighted Guest)
// =========================================================================
const GuestFeedbackScene = () => (
  <svg viewBox="0 0 320 280" className="isometric-scene-svg" aria-label="Guest Feedback Platform">
    <defs>
      <linearGradient id="topFaceGlow6" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="60%" stopColor="#FDF2F8" />
        <stop offset="100%" stopColor="#FCE7F3" />
      </linearGradient>
      <linearGradient id="leftFace6" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#CBD5E1" />
        <stop offset="100%" stopColor="#94A3B8" />
      </linearGradient>
      <linearGradient id="rightFace6" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#64748B" />
      </linearGradient>
      <linearGradient id="goldStarGrad6" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FEF08A" />
        <stop offset="40%" stopColor="#FBBF24" />
        <stop offset="70%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#B45309" />
      </linearGradient>
      <linearGradient id="roseGoldBase" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F472B6" />
        <stop offset="50%" stopColor="#DB2777" />
        <stop offset="100%" stopColor="#831843" />
      </linearGradient>
      <radialGradient id="pinkGlow6" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#F472B6" stopOpacity="0.8" />
        <stop offset="50%" stopColor="#EC4899" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#EC4899" stopOpacity="0" />
      </radialGradient>
      <filter id="softGlow6" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="3.5" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Platform Ground Drop Shadow */}
    <ellipse cx="160" cy="225" rx="115" ry="34" fill="#0F172A" opacity="0.14" />

    {/* Podium Base */}
    <polygon points="45,190 160,235 160,252 45,207" fill="url(#leftFace6)" />
    <polygon points="160,235 275,190 275,207 160,252" fill="url(#rightFace6)" />
    <polygon points="160,145 275,190 160,235 45,190" fill="url(#topFaceGlow6)" stroke="#FFFFFF" strokeWidth="1.5" />
    <polyline points="45,190 160,235 275,190" fill="none" stroke="#FFFFFF" strokeWidth="1.2" opacity="0.9" />

    {/* Radiant Pink / Magenta Core Aura */}
    <circle cx="160" cy="130" r="50" fill="url(#pinkGlow6)" />

    {/* 3D LUXURY 5-STAR REPUTATION TROPHY STAND */}
    <g transform="translate(160, 165)">
      {/* Trophy Ground Shadow */}
      <ellipse cx="0" cy="22" rx="46" ry="14" fill="#000000" opacity="0.22" />

      {/* Tiered Marble & Rose-Gold Plinth */}
      {/* Bottom Plinth */}
      <polygon points="-46,8 0,22 46,8 0,-6" fill="url(#roseGoldBase)" stroke="#9D174D" strokeWidth="1" />
      <polygon points="-46,8 0,22 0,28 -46,14" fill="#4C0519" />
      <polygon points="0,22 46,8 46,14 0,28" fill="#70092B" />

      {/* Top Plinth Tier */}
      <polygon points="-38,2 0,14 38,2 0,-10" fill="#FFFFFF" stroke="#F472B6" strokeWidth="1" />
      <polygon points="-38,2 0,14 0,18 -38,6" fill="#BE185D" />
      <polygon points="0,14 38,2 38,6 0,18" fill="#9D174D" />

      {/* 5 LARGE GLEAMING 3D GOLDEN STARS */}
      <g transform="translate(0, -8)">
        {[-32, -16, 0, 16, 32].map((xOffset, i) => {
          const yOffset = -Math.cos((i - 2) * 0.45) * 5;
          return (
            <g key={i} transform={`translate(${xOffset}, ${yOffset})`}>
              {/* Star Shadow */}
              <ellipse cx="0" cy="11" rx="5.5" ry="2" fill="#000000" opacity="0.25" />
              {/* Star Body */}
              <path
                d="M 0 -11 L 3.2 -3.4 L 11.2 -2.2 L 5.4 -0.3 L 6.8 7.8 L 0 3.8 L -6.8 7.8 L -5.4 -0.3 L -11.2 -2.2 L -3.2 -3.4 Z"
                fill="url(#goldStarGrad6)"
                stroke="#FEF08A"
                strokeWidth="0.9"
                filter="url(#softGlow6)"
              />
              <circle cx="0" cy="-2" r="1.5" fill="#FFFFFF" opacity="0.8" />
            </g>
          );
        })}
      </g>
    </g>

    {/* HOLOGRAPHIC VERIFIED GUEST TESTIMONIAL DISPLAY */}
    <g transform="translate(160, 80)">
      {/* Card Stand Legs */}
      <line x1="-26" y1="30" x2="-26" y2="44" stroke="#475569" strokeWidth="2" />
      <line x1="26" y1="30" x2="26" y2="44" stroke="#475569" strokeWidth="2" />

      {/* Review Screen Body */}
      <rect x="-68" y="-34" width="136" height="64" rx="9" fill="#0F172A" stroke="#EC4899" strokeWidth="1.6" filter="url(#softGlow6)" />

      {/* Guest Avatar & Verification Badge */}
      <g transform="translate(-54, -18)">
        <circle cx="0" cy="0" r="8" fill="#0284C7" />
        <circle cx="0" cy="-2" r="3.5" fill="#FCD34D" />
        <path d="M -4 4 C -4 1, 4 1, 4 4 Z" fill="#FFFFFF" />
        <circle cx="6" cy="6" r="3" fill="#10D864" />
        <polyline points="4.5,6 5.5,7 7.5,5" fill="none" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" />

        <text x="14" y="-1" fill="#FFFFFF" fontSize="7" fontWeight="800">
          VERIFIED GUEST
        </text>
        <text x="14" y="7" fill="#FACC15" fontSize="7.5" fontWeight="900" letterSpacing="0.05em">
          ★★★★★ 5.0
        </text>
      </g>

      {/* Clean Review Quote */}
      <text x="0" y="8" fill="#F8FAFC" fontSize="6.8" fontWeight="600" textAnchor="middle">
        "Spotless room, fast service &amp; 5★ stay!"
      </text>

      {/* Well-proportioned Satisfaction Capsule */}
      <g transform="translate(0, 19)">
        <rect x="-48" y="-5.5" width="96" height="11" rx="5.5" fill="#831843" stroke="#F472B6" strokeWidth="0.8" />
        <text x="0" y="2.5" fill="#FEF08A" fontSize="5.4" fontWeight="800" textAnchor="middle" letterSpacing="0.02em">
          ★ 99% GUEST SATISFACTION
        </text>
      </g>
    </g>

    {/* Floating 4.9 ★ RATING Metric Badge */}
    <g transform="translate(196, 28)">
      <rect x="0" y="0" width="70" height="22" rx="11" fill="#831843" stroke="#F472B6" strokeWidth="1.5" />
      <text x="35" y="15" fill="#FEF08A" fontSize="9" fontWeight="800" textAnchor="middle">
        4.9 ★ RATING
      </text>
    </g>

    {/* Figures: Delighted Guest & Smiling General Manager */}
    {/* Delighted Guest with Raised Hands */}
    <g transform="translate(75, 148)">
      <ellipse cx="0" cy="38" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="4" r="5" fill="#FDE68A" />
      <path d="M -4 2 C -4 -2, 4 -2, 4 2 Z" fill="#1E293B" />
      <rect x="-6" y="10" width="12" height="18" rx="2" fill="#EC4899" />
      <line x1="-3" y1="28" x2="-3" y2="38" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="28" x2="3" y2="38" stroke="#1E293B" strokeWidth="3" strokeLinecap="round" />
      {/* Hand Raised Cheering */}
      <line x1="-5" y1="14" x2="-12" y2="6" stroke="#EC4899" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="-13" cy="5" r="1.5" fill="#FDE68A" />
      <line x1="5" y1="14" x2="14" y2="18" stroke="#EC4899" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="15" cy="18" r="1.5" fill="#FDE68A" />
    </g>

    {/* General Manager Figure */}
    <g transform="translate(245, 148)">
      <ellipse cx="0" cy="38" rx="8" ry="3" fill="#000000" opacity="0.2" />
      <circle cx="0" cy="4" r="5" fill="#FCD34D" />
      <path d="M -4 2 C -4 -2, 4 -2, 4 2 Z" fill="#78350F" />
      {/* Navy Suit & Gold Tie */}
      <rect x="-6" y="10" width="12" height="18" rx="2" fill="#0F172A" />
      <line x1="0" y1="10" x2="0" y2="18" stroke="#F59E0B" strokeWidth="1.5" />
      <line x1="-3" y1="28" x2="-3" y2="38" stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      <line x1="3" y1="28" x2="3" y2="38" stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {/* Welcoming Hand */}
      <line x1="-5" y1="16" x2="-14" y2="18" stroke="#0F172A" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="-15" cy="18" r="1.5" fill="#FCD34D" />
    </g>
  </svg>
);

// =========================================================================
// MAIN ISOMETRIC SHOWCASE COMPONENT
// =========================================================================
const IsometricWhyChooseUs = () => {
  const [hoveredStage, setHoveredStage] = useState(null);
  const [selectedStage, setSelectedStage] = useState('guest-experience');

  const renderScene = (stageId) => {
    switch (stageId) {
      case 'guest-experience':
        return <GuestExperienceScene />;
      case 'cost-savings':
        return <CostSavingsScene />;
      case 'revenue-growth':
        return <RevenueGrowthScene />;
      case 'realtime-visibility':
        return <RealtimeVisibilityScene />;
      case 'operational-efficiency':
        return <OperationalEfficiencyScene />;
      case 'guest-feedback':
        return <GuestFeedbackScene />;
      default:
        return null;
    }
  };

  return (
    <div className="isometric-showcase-wrapper">
      {/* 6-Column Isometric Stepped Roadmap */}
      <div className="isometric-roadmap-grid">
        {STAGES.map((stage) => {
          const isTop = stage.position === 'top';
          const isCurrent = (hoveredStage || selectedStage) === stage.id;

          return (
            <div
              key={stage.id}
              className={`isometric-column ${isTop ? 'col-raised' : 'col-lower'} ${
                isCurrent ? 'is-selected' : ''
              }`}
              style={{
                '--col-color': stage.color,
                '--col-accent': stage.accentColor
              }}
              onMouseEnter={() => setHoveredStage(stage.id)}
              onMouseLeave={() => setHoveredStage(null)}
              onClick={() => setSelectedStage(stage.id)}
            >
              {/* TOP LABEL (for Raised Columns: 01 GUEST EXPERIENCE, 03 REVENUE GROWTH, 05 OPERATIONAL EFFICIENCY) */}
              {isTop && (
                <div className="label-block label-block-top">
                  <h3 className="stage-title">
                    <span className="title-row">{stage.titleLine1}</span>
                    <span className="title-row">{stage.titleLine2}</span>
                  </h3>
                  <div className="connector-vertical-line line-downward">
                    <span className="connector-dot connector-dot-top" />
                    <span className="connector-stem" />
                    <span className="connector-dot connector-dot-bottom" />
                  </div>
                </div>
              )}

              {/* ISOMETRIC PLATFORM PODIUM CARD */}
              <div className="isometric-platform-card">
                <div className="platform-stage-visual">
                  {renderScene(stage.id)}
                </div>

                {/* Subtle Hover Spotlight Glow */}
                <div
                  className="platform-hover-glow"
                  style={{ '--stage-accent': stage.accentColor }}
                />
              </div>

              {/* BOTTOM LABEL (for Lower Columns: 02 COST SAVINGS, 04 REAL-TIME VISIBILITY, 06 GUEST FEEDBACK) */}
              {!isTop && (
                <div className="label-block label-block-bottom">
                  <div className="connector-vertical-line line-upward">
                    <span className="connector-dot connector-dot-top" />
                    <span className="connector-stem" />
                    <span className="connector-dot connector-dot-bottom" />
                  </div>
                  <h3 className="stage-title">
                    <span className="title-row">{stage.titleLine1}</span>
                    <span className="title-row">{stage.titleLine2}</span>
                  </h3>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IsometricWhyChooseUs;
